import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { QUICK_CREATE_ITEMS } from "@/lib/constants";
import { useAuth } from "@/hooks/useAuth";
import { canWrite } from "@/lib/permissions/permissions";

// Each key routes to the module page with `?new=1`; every page listens to that
// param and opens its own create drawer with proper defaults.
const KEY_TO_MODULE = {
  order: "orders",
  inbound: "inbound",
  return: "returns",
  client: "clients",
  store: "stores",
};
const KEY_TO_ROUTE = {
  order: "/orders?new=1",
  inbound: "/inbound?new=1",
  return: "/returns?new=1",
  client: "/clients?new=1",
  store: "/stores?new=1",
};

export function QuickCreateMenu() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const items = QUICK_CREATE_ITEMS.filter((i) => canWrite(user?.role, KEY_TO_MODULE[i.key]));

  const handleCreate = (key) => {
    const route = KEY_TO_ROUTE[key];
    if (!route) return;
    navigate(route);
  };

  if (items.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          className="h-9 gap-1.5 pl-2.5 pr-3 shadow-[0_6px_18px_-6px_hsl(var(--primary)/0.5)]"
          data-testid="quick-create-trigger"
        >
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline">Create</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="glass-strong shadow-premium w-56 rounded-2xl border-border/60 p-1.5">
        <DropdownMenuLabel className="overline px-2 py-1.5">Quick Create</DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-border/60" />
        {items.map(({ key, label, icon: Icon }) => (
          <DropdownMenuItem
            key={key}
            onSelect={() => handleCreate(key)}
            data-testid={`quick-create-${key}`}
            className="cursor-pointer gap-2.5 rounded-lg py-2 focus:bg-primary/10"
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-inset ring-primary/20">
              <Icon className="h-3.5 w-3.5" />
            </span>
            <span className="text-sm font-medium">{label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
